import React, { FC, ReactNode, Ref, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import {
  Column,
  ColumnInstance,
  Filters,
  Hooks,
  IdType,
  Row,
  SortByFn,
  SortingRule,
  TableInstance,
  useColumnOrder,
  useFilters,
  useFlexLayout,
  useGlobalFilter,
  UseGlobalFiltersOptions,
  useResizeColumns,
  useSortBy,
  useTable,
} from 'react-table';
import useRowActions from '../DataGridRowAction/useRowActions';
import {
  CustomTableBodyProps,
  CustomTableRowProps,
  TableBodyComponentProps,
  TableRowComponentProps,
} from './TableBodyComponentContainer';

import { OnScrollArgs } from '@fluentsms/utils';
import { Card, CardProps, TableBody, TableRow } from '@material-ui/core';
import DataGridAppliedFilters from '../DataGridAppliedFilters';
import { ColumnFilter } from '../DataGridFilterMenu';
import DataGridNoDataBody, { DataGridNoDataBodyProps } from '../DataGridNoDataBody';
import { DataGridCardHeader } from './DataGridCardHeader';
import { customAlphaNumSort, customUpdatedTimestampSort } from './helper/datagrid.helper';
import { useColumnVisibility, useDataGridState, useMappedAndPersistedColumns, useOriginalColumnOrder } from './hooks';
import { useDebounceSearch } from './hooks/useDebounceSearch';
import { TableContainerWrapper } from './TableContainerWrapper';
export type EnumFilterableColumn<D extends Record<string, unknown>> = Column<D> & {
  isEnumFilterColumn?: boolean;
  enumFilterOptions?: string[];
};
export type MultiSelectFilterableColumn<D extends Record<string, unknown>> = Column<D> & {
  isMultiselectColumn?: boolean;
  multiselectFilterOptions?: string[];
  enableSelectAll?: boolean;
};

export type VirtualizeOptions = {
  rowHeight?: number;
  /** The amount of padding to decrease for the virtualized view */
  tablePadding?: number;
};

export type PersistSettingsOptions = {
  namespace: string;
  tableId: string;
  userId?: string;
  disablePersistence?: boolean;
};

export type DataGridProps<
  TRowData extends Record<string, unknown>,
  TBodyProps extends Record<string, unknown> = Record<string, unknown>
> = {
  title?: ReactNode;
  overrideTitleColor?: boolean;
  /** Object containing table data */
  data?: TRowData[];
  /** Object Containing Table Column information */
  columns?: (Column<TRowData> | EnumFilterableColumn<TRowData> | MultiSelectFilterableColumn<TRowData>)[];
  /** Pass additional hooks to the useTable hook */
  pluginHooks?: { (hooks: Hooks<TRowData>): void }[];
  /** Enable/Disable PluginHooks that are included in the Data Grid by default via react-table. */
  tableOptions?: { [key: string]: any }; // eslint-disable-line @typescript-eslint/no-explicit-any
  /** Allow for custom filtering options */
  useGlobalFilterOptions?: UseGlobalFiltersOptions<TRowData>;
  /** Enable support for row actions */
  renderRowAction?: (row: { row: Row<TRowData> }) => JSX.Element;
  /** Row click callback */
  onRowClick?: (row: Row<TRowData>) => void;
  /** Will display loading skeleton if true */
  loading?: boolean;
  /** Enable virtualized rendering */
  virtualize?: boolean;
  /** Customize height of table and row when virtual rendering is enabled. Dependent on 'virtualize' prop being 'true'. */
  virtualizeOptions?: VirtualizeOptions;
  /**
   * Whether or not the export option button is rendered.
   *
   * @default true
   * */
  showExportMenu?: boolean;
  /**
   * The name of the Excel spreadsheet that gets exported.
   * Please provide WITHOUT the file extension.
   *
   * @example 'Demo File Name' results in an Excel spreadsheet named 'Demo File Name.xlsx'
   * @example 'Demo File Name.xlsx' results in an Excel spreadsheet named 'Demo File Name.xlsx.xlsx'
   *
   * @default 'Download'
   * */
  excelExportFileName?: string;
  /**
   * The name of the Excel worksheet within the workbook.
   *
   * @default 'Data'
   * */
  excelExportSheetName?: string;
  /**
   * Callback called when Export to XLSX button is clicked.
   *
   * Ideally, you would eager-load any paginated data in this callback
   * and ensure you are passing a value for `loading`
   * so that the export includes all available data.
   */
  onExportClick?: (event: React.MouseEvent) => void;
  /** Whether or not the settings button is rendered. Defaults to `true` */
  showSettingsMenu?: boolean;
  /** Whether or not the filter button is rendered. Defaults to `true` */
  showFilterMenu?: boolean;
  /** Callback that takes `useTable` state for `sortBy`. This props should be used in conjunction with `tableOptions={ manualSortBy: true }` */
  onSortByChange?: (sortByValues: SortingRule<TRowData>[]) => void;
  /** Callback that takes `useTable` state for `globalFilter`. This props should be used in conjunction with `tableOptions={ manualGlobalFilter: true }` */
  onSearchTermChange?: (searchValue: string) => void;
  /** Callback that takes `useTable` state for `filters`. This props should be used in conjunction with `tableOptions={ manualFilters: true }` */
  onFilterChange?: (filters: Filters<TRowData>) => void;
  /** Callback use for FixedSizeList to inject business logic during scrolling */
  onScroll?: (args: OnScrollArgs) => void;
  /** Object containing buttons that will be passed to the left side of the header */
  leftAlignedButtons?: ReactNode[];
  /** Object containing buttons that will be passed to the left side of the header */
  renderLeftAlignedButtons?: (instance: TableInstance<TRowData>) => ReactNode;
  /** Object containing buttons that will be passed to the right side of the header, following the pre-existing Datagrid buttons */
  rightAlignedButtons?: ReactNode[];
  /** Displays custom table body content, wrapped inside of the table body itself. */
  TableBodyComponent?: (tableBodyProps: CustomTableBodyProps & TableBodyComponentProps<TBodyProps>) => JSX.Element;
  /** Specifies props to be passed in to the custom table body.  Defaults to {component="div"}. */
  TableBodyComponentProps?: TableBodyComponentProps<TBodyProps>;
  /** Displays custom table row content, wrapped inside of the table row itself. */
  TableRowComponent?: ({ children }: CustomTableRowProps) => JSX.Element;
  /** Specifies props to be passed in to the custom table row. */
  TableRowComponentProps?: TableRowComponentProps;
  /** Specifies props to be passed in to the card container.  Defaults to {}. */
  /** renders a custom no data component, if there is no data in the table. defaults to
 <DataGridNoDataBody/> */
  NoDataComponent?: FC<DataGridNoDataBodyProps>;
  /** Props to pass to NoDataComponent */
  NoDataComponentProps?: DataGridNoDataBodyProps;
  CardProps?: CardProps;
  /** Sets an initial value on the data grid search input box */
  searchBarInput?: string;
  /** Settings Persistence props. If either id or namespace are not provided DataGrid does not persist settings. */
  /** Additionally a disablePersistence flag is optional to allow programatic control over persistence */
  persistSettingsOptions?: PersistSettingsOptions;
  /** Exposes a reference to the react-table instance. */
  tableRef?: React.Ref<TableInstance<TRowData> | undefined>;
  /** Exposes a reference to the underlying mui table element */
  muiTableRef?: Ref<HTMLDivElement>;
  /** Should the CardHeader sit on top of the grid? */
  showHeader?: boolean;
  toolTipOptions?: { searchToolTipText?: string };
};

export function DataGrid<
  TRowData extends Record<string, unknown>,
  TBodyProps extends Record<string, unknown> = Record<string, unknown>
>({
  data = [],
  columns = [],
  title,
  overrideTitleColor = false,
  pluginHooks = [],
  tableOptions,
  showExportMenu = true,
  showSettingsMenu = true,
  showFilterMenu = true,
  useGlobalFilterOptions,
  renderRowAction,
  onRowClick = () => undefined,
  loading = false,
  virtualize = true,
  virtualizeOptions,
  onSortByChange,
  onSearchTermChange,
  onFilterChange,
  onScroll,
  leftAlignedButtons,
  renderLeftAlignedButtons,
  rightAlignedButtons,
  TableBodyComponent = TableBody,
  TableBodyComponentProps,
  TableRowComponent = TableRow,
  TableRowComponentProps = { component: 'div' },
  NoDataComponent = DataGridNoDataBody,
  NoDataComponentProps = {},
  CardProps = {},
  searchBarInput,
  persistSettingsOptions,
  excelExportFileName = 'Download',
  excelExportSheetName = 'Data',
  onExportClick,
  tableRef,
  showHeader = true,
  toolTipOptions,
  muiTableRef,
}: DataGridProps<TRowData, TBodyProps>): JSX.Element {
  const defaultColumn = useMemo(
    () => ({
      minWidth: 56,
      width: 160,
      maxWidth: 400,
    }),
    [],
  );

  /**
   * Note: react-table's alphanumerical sort does not behave expectedly https://github.com/tannerlinsley/react-table/issues/2909#issuecomment-740222063.
   * Unless the consumer specifies the type of sort they want or provide their own custom sorting logic, we will always use our custom alphanumerical sort.
   */

  const sortTypes: Record<string, SortByFn<TRowData>> = {
    customAlphaNumericSort: useCallback(customAlphaNumSort, []),
    customUpdatedTimestampSort: useCallback(customUpdatedTimestampSort, []),
  };

  const { state: persistedGridState, setState: setPersistedGridState, shouldPersist } = useDataGridState(
    persistSettingsOptions,
  );

  useMappedAndPersistedColumns(columns, shouldPersist, persistedGridState);

  const tableInstance: TableInstance<TRowData> = useTable(
    {
      data,
      columns,
      defaultColumn,
      sortTypes,
      ...tableOptions,
      ...(useGlobalFilterOptions && useGlobalFilterOptions),
      autoResetSortBy: !shouldPersist,
      autoResetFilters: !shouldPersist,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    useFlexLayout,
    useResizeColumns,
    useRowActions(renderRowAction as any), // eslint-disable-line @typescript-eslint/no-explicit-any
    useColumnOrder,
    ...pluginHooks,
  );

  useImperativeHandle(tableRef, () => tableInstance, [tableInstance]);

  const {
    getTableProps,
    headerGroups,
    rows,
    prepareRow,
    setGlobalFilter,
    state: { globalFilter, filters, sortBy },
    setHiddenColumns,
    toggleHideColumn,
    setColumnOrder,
    headers,
    setAllFilters,
    columns: columnInstances,
    allColumns,
    visibleColumns,
  } = tableInstance;

  // Restore grid state if persisted settings are being used and exist
  useEffect(() => {
    if (shouldPersist && persistedGridState) {
      persistedGridState.sortBy && tableInstance.setSortBy(persistedGridState.sortBy);
      persistedGridState.filters && tableInstance.setAllFilters(persistedGridState.filters);
      persistedGridState.columnOrder && tableInstance.setColumnOrder(persistedGridState.columnOrder);

      const hiddenColumnsCount = persistedGridState?.hiddenColumns?.length;
      const allColumnCount = columns.length;

      if (hiddenColumnsCount && hiddenColumnsCount < allColumnCount) {
        persistedGridState.hiddenColumns && tableInstance.setHiddenColumns(persistedGridState.hiddenColumns);
      }
    }
  }, []);

  useEffect(() => {
    if (shouldPersist && persistedGridState) {
      const initHiddenColumns =
        (tableOptions && tableOptions['initialState'] && tableOptions['initialState']['hiddenColumns']) ?? [];
      const remainingVisibleColumns = visibleColumns.filter((col) => col.id !== 'Menu');
      const allColumnsVisible = columns.length - remainingVisibleColumns.length === 0;

      if (allColumnsVisible && initHiddenColumns.length > 0) {
        persistedGridState.hiddenColumns && tableInstance.setHiddenColumns(['all-columns-displayed']);
      }
    }
  }, [visibleColumns.length, shouldPersist]);

  // Store grid state on table instance state change
  useEffect(() => {
    const updateStatePayload = { ...tableInstance.state };

    const hasResizedColumns =
      !!tableInstance.state.columnResizing && Object.keys(tableInstance.state.columnResizing).length > 1;

    // If the latest table state does not include `resizingColumns` state information, retain the old resizingColumns value
    // Otherwise the persisted column width information is lost on the first reload, and cannot be applied on subsequent reloads
    if (!hasResizedColumns && persistedGridState) {
      updateStatePayload.columnResizing = persistedGridState.columnResizing;
    }

    shouldPersist && setPersistedGridState?.(updateStatePayload);
  }, [tableInstance.state]);

  const [tableStateChangeListener, setTableStateChangeListener] = useState(false);

  const [displayNoColumnMessage, setDisplayNoColumnMessage] = useState(false);

  useEffect(() => {
    if (onSortByChange) {
      onSortByChange(sortBy);
    }
  }, [sortBy]);

  useEffect(() => {
    if (onSearchTermChange && globalFilter !== undefined) {
      onSearchTermChange(globalFilter);
    }
  }, [globalFilter]);

  useEffect(() => {
    if (onFilterChange && filters) {
      onFilterChange(filters);
    }
  }, [filters]);

  const { columns: originalColumnOrder } = useOriginalColumnOrder(allColumns);

  const handleChangeColumnVisibility = (hiddenColumn: ColumnInstance<TRowData>) => {
    const { columnId, currentHiddenColumn } = useColumnVisibility(hiddenColumn, tableOptions, useGlobalFilterOptions);

    toggleHideColumn(columnId);
    handleNoColumnDisplayedMessage(currentHiddenColumn);
  };

  const handleNoColumnDisplayedMessage = (hiddenColumn: ColumnInstance<TRowData>) => {
    const remainingColumns = visibleColumns.filter((col) => col.id !== hiddenColumn.id);
    const noColumnsDisplayed = remainingColumns.length === 0;
    const onlyMenuColumnDisplayed = remainingColumns.length === 1 && remainingColumns[0].id === 'Menu';
    const hiddenColumnIsBeingRemoved = hiddenColumn.disableGlobalFilter === true;
    if (hiddenColumnIsBeingRemoved && (noColumnsDisplayed || onlyMenuColumnDisplayed)) {
      setDisplayNoColumnMessage(true);
    } else if (displayNoColumnMessage === true) {
      setDisplayNoColumnMessage(false);
    }
  };

  const handleChangeColumnOrder = (reorderedColumn: IdType<TRowData>[]) => {
    setColumnOrder(reorderedColumn);
  };

  const resetColumnFilter = () => {
    allColumns.forEach((column) => {
      if (
        useGlobalFilterOptions?.disableGlobalFilter !== false &&
        tableOptions?.disableGlobalFilter !== false &&
        column.disableGlobalFilter !== false
      ) {
        column.disableGlobalFilter = false;
      }
    });
  };

  const onReset = () => {
    const initHiddenColumns =
      (tableOptions && tableOptions['initialState'] && tableOptions['initialState']['hiddenColumns']) ?? [];
    setHiddenColumns(initHiddenColumns);
    resetColumnFilter();
    setColumnOrder(originalColumnOrder);
    setDisplayNoColumnMessage(false);
  };

  const onDelete = (columnFilter: Filters<ColumnFilter>) => {
    setAllFilters(columnFilter);
  };

  const handleDebounceSearch = useDebounceSearch(500, setGlobalFilter, onSearchTermChange);

  return (
    <>
      <Card data-testid="data-grid-card" {...CardProps}>
        {showHeader && (
          <DataGridCardHeader
            overrideTitleColor={overrideTitleColor}
            title={title}
            handleDebounceSearch={handleDebounceSearch}
            onExportClick={onExportClick}
            renderLeftAlignedButtons={renderLeftAlignedButtons}
            tableInstance={tableInstance}
            leftAlignedButtons={leftAlignedButtons}
            rightAlignedButtons={rightAlignedButtons}
            showExportMenu={showExportMenu}
            showSettingsMenu={showSettingsMenu}
            showFilterMenu={showFilterMenu}
            data={data}
            excelExportFileName={excelExportFileName}
            excelExportSheetName={excelExportSheetName}
            columnInstances={columnInstances}
            filters={filters}
            setAllFilters={setAllFilters}
            headers={headers}
            handleChangeColumnVisibility={handleChangeColumnVisibility}
            handleChangeColumnOrder={handleChangeColumnOrder}
            onReset={onReset}
            tableOptions={tableOptions}
            useGlobalFilterOptions={useGlobalFilterOptions}
            globalFilter={globalFilter}
            searchBarInput={searchBarInput}
            toolTipOptions={toolTipOptions}
          />
        )}
        <DataGridAppliedFilters
          columns={(headers as unknown) as ColumnInstance[]}
          columnFilters={filters}
          onDelete={onDelete}
        />
        <TableContainerWrapper
          ref={muiTableRef}
          virtualize={virtualize}
          displayNoColumnMessage={displayNoColumnMessage}
          loading={loading}
          tableOptions={tableOptions}
          allColumns={allColumns as any} // TODO: Fix typing
          headerGroups={headerGroups as any} // TODO: Fix typing
          rows={rows as any} // TODO: Fix typing
          data={data}
          onRowClick={onRowClick as any} // TODO: Fix typing
          prepareRow={prepareRow as any} // TODO: Fix typing
          tableStateChangeListener={tableStateChangeListener}
          virtualizeOptions={virtualizeOptions}
          onScroll={onScroll}
          TableBodyComponent={TableBodyComponent as any} // TODO: Fix typing
          TableBodyComponentProps={TableBodyComponentProps}
          TableRowComponent={TableRowComponent}
          TableRowComponentProps={TableRowComponentProps}
          noDataComponent={NoDataComponent}
          noDataComponentProps={NoDataComponentProps}
          getTableProps={getTableProps as any} // TODO: Fix typing
          setTableStateChangeListener={setTableStateChangeListener}
          filters={filters}
          globalFilter={globalFilter}
        />
      </Card>
    </>
  );
}

export default DataGrid;
